type EventCallback = (data: unknown) => void

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

class RealtimeClient {
  private es: EventSource | null = null
  private listeners: Map<string, Set<EventCallback>> = new Map()
  private esHandlers: Map<string, (e: MessageEvent) => void> = new Map()
  private token: string | null = null

  connect(token: string): void {
    if (!token) {
      console.warn('[SSE] connect() called without token, skipping')
      return
    }

    if (this.es && this.es.readyState === EventSource.OPEN && this.token === token) {
      console.log('[SSE] Already connected with same token, skipping')
      return
    }

    this.disconnect()
    this.token = token

    const url = `${API_URL}/api/realtime/events?token=${encodeURIComponent(token)}`
    console.log('[SSE] Connecting to', url.split('?')[0])

    this.es = new EventSource(url)

    this.es.onopen = () => {
      console.log('[SSE] Connection established to api.1conecta.com')
    }

    this.es.onerror = () => {
      const state = this.es?.readyState
      const states = ['CONNECTING', 'OPEN', 'CLOSED']
      console.warn(`[SSE] Connection error — state: ${states[state ?? 0]} (${state})`)
      if (this.es?.readyState === EventSource.CLOSED) {
        console.warn('[SSE] Connection permanently closed — browser will NOT retry automatically')
      }
    }

    for (const eventName of this.listeners.keys()) {
      this.attachESHandler(eventName)
    }
  }

  private attachESHandler(eventName: string): void {
    if (!this.es || this.esHandlers.has(eventName)) return

    const handler = (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data)
        this.dispatch(eventName, data)
      } catch { /* ignore parse errors */ }
    }
    this.es.addEventListener(eventName, handler)
    this.esHandlers.set(eventName, handler)
  }

  on(event: string, callback: EventCallback): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event)!.add(callback)

    if (this.es) {
      this.attachESHandler(event)
    }

    return () => {
      this.listeners.get(event)?.delete(callback)
      if (this.listeners.get(event)?.size === 0) {
        this.listeners.delete(event)
        const handler = this.esHandlers.get(event)
        if (handler) {
          this.es?.removeEventListener(event, handler)
          this.esHandlers.delete(event)
        }
      }
    }
  }

  disconnect(): void {
    if (this.es) {
      console.log('[SSE] Disconnecting')
      this.es.close()
      this.es = null
    }
    this.esHandlers.clear()
  }

  private dispatch(event: string, data: unknown): void {
    this.listeners.get(event)?.forEach(cb => {
      try { cb(data) } catch { /* ignore handler errors */ }
    })
  }
}

export const realtime = new RealtimeClient()
